/**
 * Logout Utility
 *
 * Centralized logout function that preserves the current domain
 * and redirects to the appropriate landing page after logout.
 *
 * This ensures consistent logout behavior across all components.
 * Uses manual redirect instead of NextAuth's redirect to guarantee
 * we stay on the current domain (not NEXTAUTH_URL).
 */

import { signOut } from 'next-auth/react'

/**
 * Handles user logout with domain preservation
 *
 * @param options - Optional configuration
 * @param options.lang - Language code for redirect (defaults to 'en')
 * @param options.redirectPath - Custom redirect path (defaults to `/${lang}`)
 * @param options.onComplete - Callback to run after logout is initiated
 *
 * @example
 * ```tsx
 * import { handleLogout } from 'app/utils/logout'
 *
 * const MyComponent = () => {
 *   const handleClick = () => handleLogout({ lang: 'en' })
 *   return <button onClick={handleClick}>Logout</button>
 * }
 * ```
 */
export async function handleLogout(options?: {
    lang?: string
    redirectPath?: string
    onComplete?: () => void
}): Promise<void> {
    try {
        const lang = options?.lang || 'en'
        const redirectPath = options?.redirectPath || `/${lang}`

        // CRITICAL: Capture the current origin BEFORE signOut
        // This ensures we preserve the exact domain (including .localhost)
        // even if signOut or NextAuth middleware tries to change it
        let redirectUrl: string | null = null
        if (typeof window !== 'undefined') {
            // Capture all URL parts synchronously before any async operations
            const currentProtocol = window.location.protocol
            const currentHost = window.location.host // Includes hostname:port (e.g., pfl-esports.com.localhost:3000)
            redirectUrl = `${currentProtocol}//${currentHost}${redirectPath}`

            // Debug logging to help diagnose domain preservation issues
            if (process.env.NODE_ENV === 'development') {
                console.log('[handleLogout] Pre-signOut capture:', {
                    currentOrigin: window.location.origin,
                    currentHost: window.location.host,
                    currentProtocol: window.location.protocol,
                    redirectUrl,
                    redirectPath,
                    windowLocationHref: window.location.href
                })
            }
        }

        // Run completion callback before logout (e.g., close dropdowns)
        options?.onComplete?.()

        // Sign out without redirect - NextAuth's redirect uses NEXTAUTH_URL
        // which can cause cross-domain redirects
        await signOut({
            redirect: false
        })

        // Manually redirect using the URL we captured BEFORE signOut
        // This ensures we preserve the exact domain (including .localhost)
        // and avoid any middleware/NextAuth interference
        if (typeof window !== 'undefined' && redirectUrl) {
            // Double-check the URL hasn't changed (shouldn't, but just in case)
            if (process.env.NODE_ENV === 'development') {
                console.log('[handleLogout] Post-signOut redirect:', {
                    redirectUrl,
                    currentOrigin: window.location.origin,
                    currentHost: window.location.host
                })
            }

            // Use window.location.replace() with the pre-captured URL:
            // 1. Replace current history entry (prevents back button issues)
            // 2. Ensure immediate redirect (no async delay)
            // 3. Preserve exact same domain/port (including .localhost)
            // 4. Use URL captured before signOut to avoid any async timing issues
            window.location.replace(redirectUrl)
        }
    } catch (error) {
        console.error('[handleLogout] Logout error:', error)
        // Re-throw to allow components to handle errors if needed
        throw error
    }
}
